import React from "react";

import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import {getPrimaryColor, getPrimaryAlpha} from "@/utils/theme";

const VLFSelectNew = ({
	options,
	value,
	onChange,
	label,
	placeholder = "Seleziona...",
	isMulti = false,
	isClearable = true,
	isDisabled = false,
	required = false,
	editable = true,
	sorted = true,
	sortOrder = "ascend",
	sortKey = "label",
	error = false,
	onCreateOption,
	styles = {},
	containerStyles = {},
	controlStyles = {},
	theme = {},
	tableField = false,
	...props
}) => {
	const Component = onCreateOption ? CreatableSelect : Select;
	const sortedOptions = sorted
		? options
				?.slice()
				.sort((a, b) =>
					sortOrder === "ascend"
						? a[sortKey]?.toString().localeCompare(b[sortKey])
						: b[sortKey]?.toString().localeCompare(a[sortKey]),
				)
		: options;

	// Get theme colors
	const primaryColor = getPrimaryColor();
	const primaryAlpha10 = getPrimaryAlpha(10);
	const primaryAlpha40 = getPrimaryAlpha(40);

	return (
		<>
			{label && (
				<div className="field-label-container">
					<div className="field-label">{label}</div>
					{required && editable && <div className="field-required">*</div>}
				</div>
			)}
			{editable ? (
				<>
					<Component
						options={sortedOptions}
						value={value}
						onChange={onChange}
						onCreateOption={onCreateOption}
						placeholder={placeholder}
						isMulti={isMulti}
						isClearable={isClearable}
						isDisabled={isDisabled}
						menuPortalTarget={document.body}
						styles={{
							control: (provided, state) => ({
								...provided,
								marginTop: "0px",
								marginBottom: tableField ? "0px" : "8px",
								height: tableField ? "38px" : !isMulti ? "42px" : "100%",
								minHeight: tableField ? "38px" : !isMulti ? "42px" : "100%",
								fontSize: tableField ? "12px" : "14px",
								width: tableField ? "200px" : "100%",
								...controlStyles,
								background: "linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)",
								borderRadius: "8px",
								borderColor: error
									? "#ef4444"
									: state.isFocused
										? primaryColor
										: "rgba(0, 0, 0, 0.12)",
								borderWidth: state.isFocused ? "2px" : "1.5px",
								boxShadow: state.isFocused
									? `0 0 0 3px ${primaryAlpha10}`
									: "none",
								transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
								"&:hover": {
									borderColor: error
										? "#ef4444"
										: state.isFocused
											? primaryColor
											: primaryAlpha40,
								},
								outline: "none",
								"&:focus-visible": {
									outline: "none",
									boxShadow: state.isFocused
										? `0 0 0 3px ${primaryAlpha10}`
										: "none",
								},
								cursor: isDisabled ? "not-allowed" : "pointer",
								opacity: isDisabled ? 0.7 : 1,
							}),
							container: (provided) => ({
								...provided,
								width: "100%",
								...containerStyles,
							}),
							valueContainer: (provided) => ({
								...provided,
								padding: "0 14px",
							}),
							input: (provided) => ({
								...provided,
								margin: "0px",
								padding: "0px",
								color: "#1e293b",
								fontWeight: 400,
								letterSpacing: "0.01em",
							}),
							placeholder: (provided) => ({
								...provided,
								color: "#94a3b8",
								opacity: 0.7,
							}),
							singleValue: (provided) => ({
								...provided,
								color: "#1e293b",
								fontWeight: 400,
								letterSpacing: "0.01em",
							}),
							clearIndicator: (provided) => ({
								...provided,
								color: "#64748b",
								padding: "8px",
								transition: "color 0.2s ease",
								"&:hover": {
									color: "#1059bf",
								},
							}),
							dropdownIndicator: (provided, state) => ({
								...provided,
								color: state.isFocused ? "#1059bf" : "#64748b",
								padding: "8px",
								transition: "color 0.2s ease",
								"&:hover": {
									color: "#1059bf",
								},
							}),
							option: (styles, {data, isFocused, isSelected}) => {
								return {
									...styles,
									color: data.color || "#1e293b",
									backgroundColor: isSelected
										? "rgba(45, 95, 187, 0.12)"
										: isFocused
											? "rgba(45, 95, 187, 0.06)"
											: "transparent",
									fontWeight: isSelected ? 600 : 400,
									cursor: "pointer",
									transition: "all 0.2s ease",
									"&:active": {
										backgroundColor: "rgba(16, 89, 191, 0.15)",
									},
								};
							},
							menu: (provided) => ({
								...provided,
								zIndex: 9999,
								borderRadius: "12px",
								boxShadow:
									"0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
								border: "1px solid rgba(0, 0, 0, 0.06)",
								marginTop: "4px",
							}),
							menuPortal: (provided) => ({
								...provided,
								zIndex: 9999,
							}),
							multiValue: (styles) => ({
								...styles,
								backgroundColor: "#1976d2",
								color: "#fff",
								borderRadius: "5px",
							}),
							multiValueLabel: (styles) => ({
								...styles,
								color: "white",
							}),
							multiValueRemove: (styles) => ({
								...styles,
								color: "white",
								":hover": {
									backgroundColor: "#002160",
								},
							}),
							...styles,
						}}
						menuPlacement={"auto"}
						theme={(defaultTheme) => ({
							...defaultTheme,
							...theme,
						})}
						{...props}
					/>
					{error && (
						<p
							className="MuiFormHelperText-root Mui-error"
							style={{color: "#d32f2f"}}
						>
							Campo obbligatorio
						</p>
					)}
				</>
			) : (
				<div className="field-value">{value || "---"}</div>
			)}
		</>
	);
};

export default VLFSelectNew;
